import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FileText, Send, Clock, User, CheckCircle2, X, Loader2,
  Receipt, Sparkles, ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { apiFetch } from '../../fetchInterceptor';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTENT_META = {
  create_bill: { icon: Receipt, label: 'Create draft bill', color: 'emerald' },
  send_invoice_reminder: { icon: Send, label: 'Send invoice reminder', color: 'sky' },
  log_time: { icon: Clock, label: 'Log time entry', color: 'violet' },
  lookup_contact: { icon: User, label: 'Look up contact', color: 'amber' },
  mark_invoice_paid: { icon: CheckCircle2, label: 'Mark invoice paid', color: 'emerald' },
};

/**
 * Inline action-card rendered inside the AI Hub chat when the intent router
 * detects an executable request. Lets the user tweak fields before confirming.
 */
export default function IntentActionCard({ plan, workspaceId, onResolved, onCancel }) {
  const navigate = useNavigate();
  const [params, setParams] = useState({ ...(plan.params || {}), ...resolvedDefaults(plan) });
  const [executing, setExecuting] = useState(false);

  const meta = INTENT_META[plan.intent] || INTENT_META.create_bill;
  const Icon = meta.icon;

  const setField = (k, v) => setParams((p) => ({ ...p, [k]: v }));

  const handleConfirm = async () => {
    setExecuting(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/intent/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent: plan.intent,
          workspace_id: workspaceId,
          params,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'Action failed');
        setExecuting(false);
        return;
      }
      toast.success(data.message || 'Done.');
      onResolved?.(data);
      if (data.navigate_to) {
        // Small delay so the user sees the success toast before navigating.
        setTimeout(() => navigate(data.navigate_to), 600);
      }
    } catch (e) {
      toast.error(e?.message || 'Network error');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div
      className={cn(
        'rounded-2xl border-2 border-dashed p-4 sm:p-5 my-2',
        'bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-900 dark:to-zinc-950',
        `border-${meta.color}-500/40`,
      )}
      data-testid="intent-action-card"
      data-intent={plan.intent}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={cn(
          'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0',
          `bg-${meta.color}-500/15 text-${meta.color}-500`,
        )}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
            <Sparkles className="w-3 h-3" />
            AI action detected · {Math.round((plan.confidence || 0) * 100)}% confident
          </div>
          <p className="text-sm font-semibold mt-0.5" data-testid="intent-action-summary">
            {meta.label}
          </p>
        </div>
      </div>

      {/* Intent-specific editable fields */}
      <div className="space-y-2.5 mb-4">
        {plan.intent === 'create_bill' && (
          <BillFields params={params} setField={setField} resolved={plan.resolved} />
        )}
        {plan.intent === 'send_invoice_reminder' && (
          <ReminderFields params={params} resolved={plan.resolved} />
        )}
        {plan.intent === 'log_time' && (
          <LogTimeFields params={params} setField={setField} resolved={plan.resolved} />
        )}
        {plan.intent === 'lookup_contact' && (
          <LookupFields params={params} setField={setField} resolved={plan.resolved} />
        )}
        {plan.intent === 'mark_invoice_paid' && (
          <MarkPaidFields params={params} setField={setField} resolved={plan.resolved} />
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={handleConfirm}
          disabled={executing}
          className={cn(
            'flex-1 gap-2 text-white',
            `bg-${meta.color}-500 hover:bg-${meta.color}-600`,
          )}
          data-testid="intent-action-confirm"
        >
          {executing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <CheckCircle2 className="w-4 h-4" />
          )}
          {executing ? 'Working…' : 'Confirm'}
        </Button>
        <Button
          variant="ghost"
          onClick={onCancel}
          disabled={executing}
          data-testid="intent-action-cancel"
        >
          <X className="w-4 h-4 mr-1" />
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Resolved entities (vendor/invoice/contact) the backend pre-matched.
function resolvedDefaults(plan) {
  const r = plan.resolved || {};
  const out = {};
  if (r.vendor?.contact_id) {
    out.vendor_id = r.vendor.contact_id;
    // Display the matched name (not the raw regex extract) and let edits
    // overwrite both fields cleanly.
    out.vendor_hint = r.vendor.name;
  }
  if (r.account?.account_id) out.account_id = r.account.account_id;
  if (r.customer?.contact_id) {
    out.contact_id = r.customer.contact_id;
    out.project_or_customer = r.customer.name;
  }
  if (r.invoice?.invoice_id) out.invoice_id = r.invoice.invoice_id;
  if (r.contact?.contact_id) out.matched_contact_id = r.contact.contact_id;
  return out;
}

// ─── Field groups ────────────────────────────────────────────────────────
function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[88px,1fr] sm:grid-cols-[110px,1fr] items-center gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function BillFields({ params, setField, resolved }) {
  return (
    <>
      <Row label="Amount">
        <Input
          type="number"
          step="0.01"
          value={params.amount || ''}
          onChange={(e) => setField('amount', parseFloat(e.target.value) || 0)}
          className="h-9 text-sm"
          data-testid="intent-field-amount"
        />
      </Row>
      <Row label="Vendor">
        <Input
          value={params.vendor_hint || ''}
          onChange={(e) => {
            setField('vendor_hint', e.target.value);
            setField('vendor_id', null);
          }}
          placeholder="Vendor name"
          className="h-9 text-sm"
          data-testid="intent-field-vendor"
        />
      </Row>
      <Row label="Memo">
        <Input
          value={params.memo || ''}
          onChange={(e) => setField('memo', e.target.value)}
          placeholder="What was it for?"
          className="h-9 text-sm"
          data-testid="intent-field-memo"
        />
      </Row>
      {resolved?.account && (
        <p className="text-[11px] text-muted-foreground pl-[88px] sm:pl-[110px]">
          Will post to <span className="font-mono">{resolved.account.code}</span> {resolved.account.name}
        </p>
      )}
    </>
  );
}

function ReminderFields({ params, resolved }) {
  if (!resolved?.invoice) {
    return (
      <p className="text-sm text-amber-600 dark:text-amber-400" data-testid="intent-no-invoice-match">
        Couldn't find invoice #{params.invoice_no} in this workspace.
      </p>
    );
  }
  const inv = resolved.invoice;
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-sm" data-testid="intent-invoice-summary">
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold">#{inv.invoice_no} · {inv.customer_name}</span>
        <span className="font-mono text-xs uppercase text-muted-foreground">{inv.status}</span>
      </div>
      <p className="text-xs text-muted-foreground">
        Total ${(inv.total || 0).toLocaleString()} · Balance ${(inv.balance_due || 0).toLocaleString()}
      </p>
    </div>
  );
}

function LogTimeFields({ params, setField, resolved }) {
  return (
    <>
      <Row label="Hours">
        <Input
          type="number"
          step="0.25"
          value={params.hours || ''}
          onChange={(e) => setField('hours', parseFloat(e.target.value) || 0)}
          className="h-9 text-sm"
          data-testid="intent-field-hours"
        />
      </Row>
      <Row label="Customer">
        <Input
          value={params.project_or_customer || ''}
          onChange={(e) => {
            setField('project_or_customer', e.target.value);
            setField('contact_id', null);
          }}
          placeholder="Customer / project"
          className="h-9 text-sm"
          data-testid="intent-field-customer"
        />
      </Row>
      <Row label="Note">
        <Input
          value={params.description || ''}
          onChange={(e) => setField('description', e.target.value)}
          placeholder="What did you do?"
          className="h-9 text-sm"
          data-testid="intent-field-description"
        />
      </Row>
    </>
  );
}

function LookupFields({ params, setField }) {
  return (
    <Row label="Find">
      <Input
        value={params.query || ''}
        onChange={(e) => setField('query', e.target.value)}
        placeholder="Contact name"
        className="h-9 text-sm"
        data-testid="intent-field-query"
      />
    </Row>
  );
}

function MarkPaidFields({ params, setField, resolved }) {
  if (!resolved?.invoice) {
    return (
      <p className="text-sm text-amber-600 dark:text-amber-400" data-testid="intent-no-invoice-match">
        Couldn't find invoice #{params.invoice_no} in this workspace.
      </p>
    );
  }
  const inv = resolved.invoice;
  return (
    <>
      <div className="rounded-lg border border-border bg-card p-3 text-sm">
        <div className="flex items-center justify-between mb-1">
          <span className="font-semibold">#{inv.invoice_no} · {inv.customer_name}</span>
          <span className="font-mono text-xs uppercase text-muted-foreground">{inv.status}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Balance ${(inv.balance_due || 0).toLocaleString()}
        </p>
      </div>
      <Row label="Amount">
        <Input
          type="number"
          step="0.01"
          value={params.amount ?? inv.balance_due}
          onChange={(e) => setField('amount', parseFloat(e.target.value) || 0)}
          className="h-9 text-sm"
          data-testid="intent-field-payment-amount"
        />
      </Row>
    </>
  );
}
